import { useEffect, useMemo, useRef, useState } from 'react';

type Props = {
  /** Comma-separated list (from EmbedStrings.thinkingVerbs). Single verb disables cycling. */
  verbsCsv: string;
  timerSuffix: string;
};

const VERB_ROTATION_MS = 3500;

export function TypingIndicator({ verbsCsv, timerSuffix }: Props) {
  const verbs = useMemo(() => parseVerbs(verbsCsv), [verbsCsv]);

  // Random starting index so successive turns don't always begin with the
  // same verb — small thing, makes it feel less canned.
  const startIndex = useRef(Math.floor(Math.random() * verbs.length)).current;
  const [verbIndex, setVerbIndex] = useState(startIndex);
  const startedAtRef = useRef<number>(Date.now());
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (verbs.length <= 1) return;
    const id = setInterval(() => {
      setVerbIndex((i) => (i + 1) % verbs.length);
    }, VERB_ROTATION_MS);
    return () => clearInterval(id);
  }, [verbs.length]);

  useEffect(() => {
    // Align to the next whole-second boundary so the counter ticks once per
    // displayed second instead of drifting + over-rendering at 4 Hz.
    const startedAt = startedAtRef.current;
    const msToNextSecond = 1000 - ((Date.now() - startedAt) % 1000);
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const timeoutId = setTimeout(() => {
      setSeconds(Math.floor((Date.now() - startedAt) / 1000));
      intervalId = setInterval(() => {
        setSeconds(Math.floor((Date.now() - startedAt) / 1000));
      }, 1000);
    }, msToNextSecond);
    return () => {
      clearTimeout(timeoutId);
      if (intervalId !== null) clearInterval(intervalId);
    };
  }, []);

  const verb = verbs[verbIndex] ?? verbs[0] ?? '';

  return (
    <div className="kb-typing" role="status" aria-live="polite" aria-label={verb}>
      {/* `key` retriggers the fade keyframe each cycle. */}
      <span key={verbIndex} className="kb-typing__verb">
        {verb}
      </span>
      <span className="kb-typing__dots" aria-hidden="true">
        <span className="kb-typing__dot" />
        <span className="kb-typing__dot" />
        <span className="kb-typing__dot" />
      </span>
      <span className="kb-typing__timer">
        {seconds}
        {timerSuffix}
      </span>
    </div>
  );
}

function parseVerbs(raw: string): string[] {
  const parts = raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return parts.length > 0 ? parts : ['…'];
}
